/**
 * @file index.js
 * @brief Boots the React application, authentication provider, translations, and service worker registration.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './i18n';
import { AuthProvider } from "react-oidc-context";
import { UserProfileProvider } from "./context/UserProfileContext";
import { LocalAuthProvider } from "./context/AuthContext";
import { SiteSettingsProvider, useSiteSettings } from "./context/SiteSettingsContext";
import { oidcConfig } from "./oidcConfig";

/**
 * Waits for site settings to load, then mounts AuthProvider with dynamic OIDC config.
 * Always mounts the OIDC provider (it's harmless when SSO is disabled — the login
 * screen simply won't show the SSO button).
 */
function AuthBridge({ children }) {
    const { siteSettings, loading } = useSiteSettings();

    if (loading) {
        return (
            <div className="auth-shell">
                <div className="auth-spinner" />
            </div>
        );
    }

    const authority = siteSettings?.oidc_authority || oidcConfig.authority;
    const clientId = siteSettings?.oidc_client_id || oidcConfig.client_id;

    const dynamicConfig = {
        ...oidcConfig,
        authority,
        client_id: clientId,
    };

    // Key forces AuthProvider to fully remount when SSO config changes
    const configKey = `${authority}|${clientId}`;

    return (
        <AuthProvider key={configKey} {...dynamicConfig}>
            {children}
        </AuthProvider>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <SiteSettingsProvider>
    <LocalAuthProvider>
      <AuthBridge>
        <UserProfileProvider>
          <App />
        </UserProfileProvider>
      </AuthBridge>
    </LocalAuthProvider>
  </SiteSettingsProvider>
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/notification-sw.js").catch(() => {
      // Notification fallback still works with window.Notification when available.
    });
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
